import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  Spinner,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import Swal from "sweetalert2";
import { getFirebaseAuthToken } from "../../../helpers/firebase_helper";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/** `YYYY-MM` of the month before today (UTC). */
const lastMonth = () => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
};

/** Column header with an info icon that explains it on hover. */
const Th = ({ id, label, info, className }) => (
  <th className={className}>
    {label}
    {info && (
      <>
        <i
          id={`cov-col-${id}`}
          className="ri-information-line text-muted ms-1 align-middle"
          style={{ cursor: "help" }}
        />
        <UncontrolledTooltip target={`cov-col-${id}`} placement="top">
          {info}
        </UncontrolledTooltip>
      </>
    )}
  </th>
);

const Stat = ({ label, value }) => (
  <div className="col-6 col-md">
    <div className="border rounded p-3 h-100">
      <div className="text-muted small">{label}</div>
      <div className="fs-4 fw-semibold">{value ?? 0}</div>
    </div>
  </div>
);

const authed = async (u, opts = {}) => {
  const res = await fetch(u, {
    ...opts,
    headers: { Authorization: `${await getFirebaseAuthToken()}` },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
  return json;
};

/** `2026-09` → "September 2026". */
const monthLabel = (m) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

const fmtWhen = (ms) =>
  ms
    ? new Date(ms).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

/**
 * Billing report: every member of the partner covered at the pharmacy on at
 * least one day of the month — from the coverage ledger, so members who were
 * terminated or deleted since are still on it. This is what the partner is
 * invoiced for, in arrears.
 *
 * From the 1st, the previous month's report is SAVED (a fixed copy kept in the
 * app — the invoice's source, whatever happens to any email). A saved month
 * opens on its saved copy; "Recalculate now" rebuilds it from the ledger.
 */
const PartnerCoverageReport = ({ partnerId }) => {
  const [month, setMonth] = useState(lastMonth());
  const [saved, setSaved] = useState([]);
  const [live, setLive] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const base = `${API_BASE}/dashboard/admin/partners/${partnerId}`;
  const savedReport = saved.find((r) => r.month === month);
  const showingSaved = !!savedReport && !live;
  const current = month === new Date().toISOString().slice(0, 7);

  const url = useCallback(
    (format) =>
      showingSaved
        ? `${base}/billing-reports/${month}${format ? `?format=${format}` : ""}`
        : `${base}/coverage-report?month=${month}${
            format ? `&format=${format}` : ""
          }`,
    [base, month, showingSaved],
  );

  const loadSaved = useCallback(async () => {
    try {
      const json = await authed(`${base}/billing-reports`);
      setSaved(json.reports || []);
    } catch (e) {
      // The live report still works without the list.
      setSaved([]);
    }
  }, [base]);

  useEffect(() => {
    loadSaved();
  }, [loadSaved]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await authed(url()));
    } catch (e) {
      Swal.fire("Could not load the report", e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (/^\d{4}-\d{2}$/.test(month)) load();
  }, [load, month]);

  const saveNow = async () => {
    if (savedReport) {
      const { isConfirmed } = await Swal.fire({
        title: `Re-save ${month}?`,
        html: "The saved copy is replaced with the report as it is now. The totals of the copy it replaces are kept on the report.",
        showCancelButton: true,
        confirmButtonText: "Re-save",
      });
      if (!isConfirmed) return;
    }
    setSaving(true);
    try {
      await authed(`${base}/billing-reports/${month}`, { method: "POST" });
      await loadSaved();
      setLive(false);
      Swal.fire("Saved", `The ${month} report is saved.`, "success");
    } catch (e) {
      Swal.fire("Could not save", e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    try {
      const res = await fetch(url("csv"), {
        headers: { Authorization: `${await getFirebaseAuthToken()}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `baxrx-${showingSaved ? "billing" : "coverage"}-${partnerId}-${month}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      Swal.fire("Could not download", e.message, "error");
    }
  };

  const s = data?.summary;

  return (
    <Card>
      <CardBody>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div>
            <h5 className="mb-0">Billing report</h5>
            <div className="text-muted small">
              Every member covered on at least one day of the month, including
              members terminated or deleted since. Saved here on the 1st for the
              previous month.
            </div>
          </div>
          <div className="ms-auto d-flex align-items-center gap-2">
            <Input
              type="month"
              value={month}
              max={new Date().toISOString().slice(0, 7)}
              onChange={(e) => setMonth(e.target.value)}
              style={{ width: 170 }}
            />
            {!current && (
              <Button
                color="secondary"
                outline
                onClick={saveNow}
                disabled={saving}
              >
                {saving ? "Saving…" : savedReport ? "Re-save" : "Save now"}
              </Button>
            )}
            <Button
              color="primary"
              outline
              onClick={download}
              disabled={!data?.rows?.length}
            >
              Download CSV
            </Button>
          </div>
        </div>

        {saved.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3 small">
            <span className="text-muted">Saved reports:</span>
            {saved.map((r) => (
              <Button
                key={r.month}
                size="sm"
                color={r.month === month ? "primary" : "light"}
                onClick={() => {
                  setMonth(r.month);
                  setLive(false);
                }}
              >
                {r.month} · {r.summary?.members ?? 0}
              </Button>
            ))}
          </div>
        )}

        {savedReport ? (
          <div
            className={`alert ${
              showingSaved ? "alert-success" : "alert-warning"
            } py-2 small d-flex align-items-center gap-2`}
          >
            <div>
              {showingSaved ? (
                <>
                  <b>Saved copy of {monthLabel(month)}</b> — saved{" "}
                  {fmtWhen(savedReport.generatedAt)}
                  {savedReport.generatedBy ? " by an admin" : ""}. This is what
                  the invoice is built from.
                  {savedReport.previousVersions?.length > 0 &&
                    ` Re-saved ${savedReport.previousVersions.length} time(s); the earlier copy had ${
                      savedReport.previousVersions[
                        savedReport.previousVersions.length - 1
                      ].summary?.members ?? 0
                    } members.`}
                </>
              ) : (
                <>
                  <b>{monthLabel(month)}, recalculated now</b> — the same month,
                  rebuilt from today's coverage history, to compare with the
                  saved copy (it differs only if something about that month
                  changed after it was saved). The invoice uses the saved copy.
                </>
              )}
            </div>
            <Button
              size="sm"
              color="light"
              className="ms-auto text-nowrap"
              onClick={() => setLive(!live)}
            >
              {showingSaved ? "Recalculate now" : "Back to saved copy"}
            </Button>
          </div>
        ) : (
          !current && (
            <div className="alert alert-light border py-2 small">
              {monthLabel(month)} is not saved yet — this is calculated now from
              the coverage history. It is saved automatically on the 1st of the
              next month, or now with <b>Save now</b>.
            </div>
          )
        )}

        {current && (
          <div className="alert alert-info py-2 small">
            {monthLabel(month)} is in progress: days are counted up to today. It
            is saved on the 1st of next month.
          </div>
        )}

        {loading && !data ? (
          <Spinner size="sm" color="primary" />
        ) : (
          data && (
            <>
              <div className="row g-2 mb-3">
                <Stat label="Members covered" value={s.members} />
                <Stat label="Member only (E)" value={s.memberOnly} />
                <Stat label="Family (F)" value={s.family} />
                <Stat
                  label="Covered at month end"
                  value={s.coveredAtMonthEnd}
                />
                <Stat label="Deleted since" value={s.deleted} />
              </div>
              {data.rows.length === 0 ? (
                <div className="text-muted">
                  No member was covered in {month}.
                </div>
              ) : (
                <div className="table-responsive">
                  <Table size="sm" hover className="align-middle">
                    <thead>
                      <tr>
                        <Th
                          id="member"
                          label="Member ID"
                          info="BaxRx member ID, printed on the card. It's the ID the pharmacy vendor (Bria) bills us by."
                        />
                        <Th id="name" label="Name" />
                        <Th
                          id="partner"
                          label="Partner member ID"
                          info="The partner's own ID for this member (externalId), so they can match each row to their records."
                        />
                        <Th id="group" label="Group" />
                        <Th
                          id="tier"
                          label="Tier"
                          info="E = member only, F = family. The tier on the member's last covered day of the month; if it changed during the month, the changes are shown in parentheses."
                        />
                        <Th
                          id="family"
                          label="Family"
                          info="How many family members (spouse, children) are covered with this member."
                        />
                        <Th
                          id="covered"
                          label="Covered"
                          info="First and last day of the month this member was covered at the pharmacy. For the current month, days up to today."
                        />
                        <Th
                          id="days"
                          label="Days"
                          className="text-end"
                          info="Number of days in the month the member was covered, both ends included (30 = the whole of a 30-day month). Gaps between a termination and a reactivation are not counted."
                        />
                        <Th
                          id="flags"
                          label=""
                          info="Ended: coverage ended before the month was over. Deleted: the member was removed later; they are still billed for the days they were covered."
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <tr key={r.employeeId}>
                          <td className="font-monospace">{r.memberId}</td>
                          <td>
                            {r.firstName} {r.lastName}
                          </td>
                          <td className="text-muted small">{r.externalId}</td>
                          <td className="small">{r.groupName}</td>
                          <td>
                            {r.tier}
                            {r.tiersInMonth.length > 1 && (
                              <span className="text-muted small">
                                {" "}
                                ({r.tiersInMonth.join(" → ")})
                              </span>
                            )}
                          </td>
                          <td>{r.familyCount}</td>
                          <td className="small">
                            {r.firstCoveredDay} → {r.lastCoveredDay}
                          </td>
                          <td className="text-end">{r.daysCovered}</td>
                          <td>
                            {r.deleted && (
                              <Badge color="secondary" className="fw-normal">
                                Deleted
                              </Badge>
                            )}{" "}
                            {!r.coveredAtMonthEnd && !r.deleted && (
                              <Badge color="warning" className="fw-normal">
                                Ended
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )
        )}
      </CardBody>
    </Card>
  );
};

export default PartnerCoverageReport;
